/* eslint-disable no-console */
import { useState } from 'react';
import { useRouter } from 'next/router';
import { useIsomorphicLayoutEffect } from '@react-hookz/web';

import {
  APP_BRIDGE_GLOBALS,
  APP_SOURCE_IN_APP,
  APP_SOURCE_QUERY_PARAMETER,
  APP_SOURCE_STORAGE_KEY,
  APP_WEBVIEW_USER_AGENT_MARKERS
} from '@lib/app-view';

export const APP_VERSIONS = {
  LEGACY: 'LEGACY',
  WEBVIEW: 'WEBVIEW'
} as const;

export type AppVersion = (typeof APP_VERSIONS)[keyof typeof APP_VERSIONS];

interface AppDetectionResult {
  isAppView: boolean;
  isLoading: boolean;
  appVersion: AppVersion;
}

/**
 * Detects if the app is running in mobile app context and determines version
 * Supports both legacy (source-based) and new WebView detection methods
 *
 * @returns object with isAppView boolean, isLoading state, and detected app version
 */
export const useIsAppView = (): AppDetectionResult => {
  const router = useRouter();
  const urlSource = router.query[APP_SOURCE_QUERY_PARAMETER] as string;
  const [isAppView, setIsAppView] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [appVersion, setAppVersion] = useState<AppVersion>(APP_VERSIONS.LEGACY);

  useIsomorphicLayoutEffect(() => {
    if (typeof window === 'undefined') {
      // SSR case
      setIsLoading(false);
      return;
    }

    // First check for new WebView detection
    const detectWebView = () => {
      if (APP_BRIDGE_GLOBALS.some((name) => window[name])) {
        return true;
      }

      const userAgent = navigator.userAgent || '';
      return APP_WEBVIEW_USER_AGENT_MARKERS.some((marker) =>
        userAgent.includes(marker)
      );
    };

    // Check for legacy source-based detection
    const detectLegacyApp = () => {
      if (urlSource === APP_SOURCE_IN_APP) {
        localStorage.setItem(APP_SOURCE_STORAGE_KEY, urlSource);
        return true;
      }

      // Check localStorage for stored source
      const storedSource = localStorage.getItem(APP_SOURCE_STORAGE_KEY);
      return storedSource === APP_SOURCE_IN_APP;
    };

    const isWebView = detectWebView();
    const isLegacyApp = detectLegacyApp();

    if (isWebView) {
      setIsAppView(true);
      setAppVersion(APP_VERSIONS.WEBVIEW);
    } else if (isLegacyApp) {
      setIsAppView(true);
      setAppVersion(APP_VERSIONS.LEGACY);
    } else {
      // Not in any app - shouldn't happen based on your requirements
      setIsAppView(false);
      setAppVersion(APP_VERSIONS.LEGACY); // Default fallback
    }

    setIsLoading(false);
  }, [urlSource]);

  return { isAppView, isLoading, appVersion };
};
